// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { InlineLoading } from '@carbon/react';
import { useSiteApprovals } from '../../hooks/useSiteApprovals';
import { useApprovals } from '../../hooks/useApprovals';
import PastRangeFilter, { DateRange } from '../../components/shell/PastRangeFilter';
import PastListControls, { type PastSort } from '../../components/shell/PastListControls';
import { HITL_APPROVAL_TYPE } from '../../utils/constants';
import { startOfDayIso, endOfDayIso, toDateParam, parseDateParam } from '../../utils/time';
import { useUrlState } from '../../hooks/useUrlState';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useNow } from '../../hooks/useNow';
import { PageHead, EmptyState } from '../../components/shell/ScreenHelpers';
import PageTabs from '../../components/shell/PageTabs';
import PlanCard from '../../components/shell/PlanCard';
import FinancialPanel from '../../components/shell/FinancialPanel';
import LivePlanPanel from './live/LivePlanPanel';
import { LiveRevokeButton } from './live/LiveRevokeButton';
import {
  executingPlans,
  filterAndSortPast,
  pastOutcome,
  pastOutcomeCounts,
  pastPlans,
  type PastFilter,
  pendingReviewPlans,
  todaysPlanApproval,
} from '../../services/planSelectors';
import { useSiteTime } from '../../hooks/useSiteTime';
import NoAccess from '../../components/common/NoAccess';
import { isNoAccessMessage } from '../../services/api';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

/**
 * The manager review queue. Each tab is a route under /recommendations/<slug>.
 * Operators get the read-only view of the same plans at /home/recommendations.
 */
const TABS = [
  { slug: 'financial', label: 'Financial' },
  { slug: 'live', label: 'Live' },
  { slug: 'tomorrow', label: 'Tomorrow' },
  { slug: 'past', label: 'Past recommendations' },
] as const;

type TabSlug = (typeof TABS)[number]['slug'];

const LIVE_PATH = '/recommendations/live';
/** Where the section lands when the Live tab is switched off. */
const NO_LIVE_PATH = '/recommendations/tomorrow';

/** How often the executing/past split is re-evaluated. */
const NOW_TICK_MS = 30_000;

export default function RecommendationsScreen() {
  const { tab } = useParams<{ tab: string }>();
  const { siteId, siteLabel } = useActiveSite();
  const { approvals, isFetching, error } = useSiteApprovals();
  // One timestamp for every selector so a rollover can't put a plan in two tabs.
  const now = useNow(NOW_TICK_MS);
  const { siteZone } = useSiteTime();
  // Past tab only: no range keeps the shared one-month window, a range fetches its own.
  // The range lives in the URL (?from=&to=, local days), so opening a plan and coming
  // back, which returns to the same address (PlanCard), finds the same dates and list.
  const [urlRange, patchUrl] = useUrlState({ from: '', to: '', outcome: 'all', sort: 'newest' });
  const range = useMemo<DateRange | null>(() => {
    const from = parseDateParam(urlRange.from);
    const to = parseDateParam(urlRange.to);
    return from && to && from <= to ? { from, to } : null;
  }, [urlRange.from, urlRange.to]);
  const outcome: PastFilter = ['approved', 'rejected', 'expired'].includes(urlRange.outcome)
    ? (urlRange.outcome as PastFilter)
    : 'all';
  const pastSort: PastSort = urlRange.sort === 'oldest' ? 'oldest' : 'newest';
  const setRange = (next: DateRange | null) =>
    patchUrl({
      from: next ? toDateParam(next.from) : null,
      to: next ? toDateParam(next.to) : null,
    });
  // A range that reaches today can still gain plans and see them change state, so it
  // polls like the shared window; a range wholly in the past is history and doesn't.
  const rangeReachesToday = !!range && range.to >= new Date(startOfDayIso(now));
  const ranged = useApprovals({
    siteId,
    approvalType: HITL_APPROVAL_TYPE,
    createdFrom: range ? startOfDayIso(range.from) : undefined,
    createdTo: range ? endOfDayIso(range.to) : undefined,
    enabled: !!siteId && !!range && tab === 'past',
    poll: rangeReachesToday,
  });

  // Kill switch (default on): turning it off drops the Live tab and sends its
  // bookmarks, and the bare /recommendations, to Tomorrow. Read without waiting
  // for LaunchDarkly, so the tab shows from the default while flags load.
  const liveOn = useFeatureFlag('recommendationsLiveEnabled');
  const tabs = TABS.filter(t => t.slug !== 'live' || liveOn);
  const selected = tabs.find(t => t.slug === tab);

  const review = pendingReviewPlans(approvals, now, siteZone);
  const live = executingPlans(approvals, now);
  const past = pastPlans(approvals, now, siteZone);
  const pastShown = range ? pastPlans(ranged.approvals, now, siteZone) : past;
  const pastCounts = pastOutcomeCounts(pastShown);
  const pastVisible = filterAndSortPast(pastShown, outcome, pastSort);

  // Unknown or missing tab (including the bare /recommendations) → default.
  if (!selected) return <Navigate to={liveOn ? LIVE_PATH : NO_LIVE_PATH} replace />;

  const counts: Record<TabSlug, number | null> = {
    financial: null,
    live: live.length || null,
    tomorrow: review.length,
    past: past.length,
  };

  const lists: Partial<
    Record<TabSlug, { plans: typeof approvals; empty: { big: string; sub: string } }>
  > = {
    tomorrow: {
      plans: review,
      empty: {
        big: 'No recommendations awaiting review.',
        sub: 'New plans appear here as Helios raises them, around 16:00 local time when the day-ahead optimization runs.',
      },
    },
    past: {
      plans: pastVisible,
      empty: {
        big: outcome === 'all' ? 'No recommendations to show.' : `No ${outcome} recommendations.`,
        sub:
          outcome !== 'all'
            ? 'Try another filter, or pick All to see every recommendation.'
            : range
              ? 'No plans were created in the selected dates.'
              : 'Approved, executed, rejected, and expired plans are listed here.',
      },
    },
  };

  const list = lists[selected.slug];

  const isPast = selected.slug === 'past';
  const bodyFetching = isPast && range ? ranged.isFetching : isFetching;
  const bodyCount = isPast && range ? ranged.approvals.length : approvals.length;
  const bodyError = isPast && range ? ranged.error : error;

  const renderBody = () => {
    if (bodyCount === 0 && bodyFetching) {
      return (
        <InlineLoading
          className="hs-bg-loading"
          description="Loading recommendations…"
          status="active"
        />
      );
    }
    if (bodyError && isNoAccessMessage(bodyError)) {
      return <NoAccess big="You don't have access to recommendations" />;
    }
    if (bodyError) return <EmptyState big="Couldn't load recommendations." sub={bodyError} />;

    if (selected.slug === 'financial') {
      return <FinancialPanel siteId={siteId} siteLabel={siteLabel} approvals={approvals} />;
    }

    if (selected.slug === 'live') {
      return live.length === 0 ? (
        <LivePlanPanel
          planned={false}
          approval={todaysPlanApproval(approvals, now, siteZone)}
          hasPendingReview={review.length > 0}
          siteId={siteId}
          now={now}
        />
      ) : (
        <LivePlanPanel approval={live[0]} siteId={siteId} now={now} />
      );
    }

    if (!list) return null;
    return list.plans.length === 0 ? (
      <EmptyState big={list.empty.big} sub={list.empty.sub} />
    ) : (
      <div className="hs-tiles-plan">
        {list.plans.map(approval => (
          <PlanCard
            key={approval.approvalId}
            approval={approval}
            expired={isPast && pastOutcome(approval) === 'expired'}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="hs-screen">
      <PageTabs
        tabs={tabs.map(t => ({
          key: t.slug,
          label: t.label,
          to: `/recommendations/${t.slug}`,
          count: counts[t.slug],
        }))}
        activeKey={selected.slug}
        ariaLabel="Recommendation status"
      />

      <div className="hs-screen-body">
        <PageHead
          title={selected.label}
          dateline={siteLabel}
          actions={selected.slug === 'live' && live.length > 0 ? <LiveRevokeButton /> : undefined}
        />
        {isPast && <PastRangeFilter range={range} onChange={setRange} />}
        {isPast && (
          <PastListControls
            filter={outcome}
            sort={pastSort}
            counts={pastCounts}
            onFilterChange={f => patchUrl({ outcome: f === 'all' ? null : f })}
            onSortChange={o => patchUrl({ sort: o === 'newest' ? null : o })}
          />
        )}
        {renderBody()}
      </div>
    </div>
  );
}
